
import { useState } from 'react';
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation)} ${className}`}>
      {children}
    </div>
  );
}

export default function ProductShowcase() {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    {
      id: 'anamnesy-soft',
      label: 'Anamnesy Soft',
      badge: 'Disponible',
      icon: 'ri-eye-line',
      title: 'Control Oftalmológico Integral',
      description: 'Gestiona historiales clínicos, diagnósticos, tratamientos, módulo de óptica integrado y seguimiento de pacientes oftalmológicos desde una sola plataforma intuitiva y segura.',
      features: [
        'Historiales clínicos digitales completos',
        'Registro de agudeza visual y refracción',
        'Control de presión intraocular',
        'Módulo de Óptica con inventario y ventas',
        'Gestión de recetas y prescripciones',
        'Reportes y estadísticas avanzadas',
      ],
      image: 'https://readdy.ai/api/search-image?query=modern%20ophthalmology%20software%20dashboard%20interface%20on%20large%20monitor%20screen%20showing%20eye%20examination%20data%20charts%20patient%20records%20and%20retinal%20scan%20images%20in%20a%20clean%20professional%20medical%20office%20with%20emerald%20green%20accent%20colors%20dark%20UI%20design&width=700&height=500&seq=product-showcase-1&orientation=landscape',
    },
    {
      id: 'proximamente1',
      label: 'Próximamente',
      badge: 'En desarrollo',
      icon: 'ri-heart-pulse-line',
      title: 'Más Especialidades Médicas',
      description: 'Estamos desarrollando nuevos módulos SaaS para otras especialidades médicas. Nuestro ecosistema crecerá para cubrir todas tus necesidades clínicas.',
      features: [
        'Cardiología y control cardiovascular',
        'Dermatología con análisis de imágenes',
        'Endocrinología y seguimiento metabólico',
        'Neurología y mapeo cerebral',
        'Integración entre especialidades',
        'Plataforma unificada multi-clínica',
      ],
      image: 'https://readdy.ai/api/search-image?query=futuristic%20medical%20technology%20concept%20with%20multiple%20holographic%20screens%20showing%20different%20medical%20specialties%20cardiology%20dermatology%20neurology%20data%20visualizations%20dark%20background%20with%20emerald%20green%20glowing%20accents%20modern%20clean%20design&width=700&height=500&seq=product-showcase-2&orientation=landscape',
    },
  ];

  const active = tabs[activeTab];

  return (
    <section id="producto" className="py-24 bg-gray-950">
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection animation="fade-in">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold text-emerald-400 uppercase tracking-widest mb-3">Nuestro Ecosistema</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Soluciones SaaS para la <span className="font-display italic text-emerald-400">salud visual</span>
            </h2>
            <p className="text-sm text-white/60 max-w-xl mx-auto">
              Anamnesy Soft es nuestra primera plataforma, con más productos en desarrollo para otras especialidades médicas.
            </p>
          </div>
        </AnimatedSection>

        <AnimatedSection animation="slide-up">
          <div className="flex items-center justify-center space-x-3 mb-14">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(index)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === index
                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                    : 'bg-white/10 text-white/70 hover:bg-white/15'
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                <span>{tab.label}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                  activeTab === index ? 'bg-white/20' : 'bg-white/10'
                }`}>{tab.badge}</span>
              </button>
            ))}
          </div>
        </AnimatedSection>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <AnimatedSection animation="slide-left">
            <div className="space-y-6">
              <h3 className="text-2xl lg:text-3xl font-bold text-white">{active.title}</h3>
              <p className="text-sm text-white/70 leading-relaxed">{active.description}</p>
              <div className="space-y-3 pt-2">
                {active.features.map((feature, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-emerald-400 text-xs"></i>
                    </div>
                    <span className="text-sm text-white/80">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="pt-4">
                <a href="https://anamnesysoft.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 px-7 py-3 bg-emerald-500 text-white text-sm font-semibold rounded-lg hover:bg-emerald-600 transition-all whitespace-nowrap cursor-pointer">
                  <span>{activeTab === 0 ? 'Registrarse / Ingresar' : 'Notificarme'}</span>
                  <i className="ri-arrow-right-line text-base"></i>
                </a>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="slide-right">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <div className="w-full h-80 lg:h-96">
                  <img
                    src={active.image}
                    alt={active.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              {activeTab === 0 && (
                <div className="absolute -top-3 -right-3 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-full shadow-lg uppercase tracking-wider">
                  Producto Estrella
                </div>
              )}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
